import type React from "react";
import { card, theme, type as text } from "../../theme";
import { RulerProgress } from "../Drawing";
import { CONTROL_STATUS_EXPERT, CONTROL_STATUS_NOVICE } from "../controlStatus";

// The summary above every standard's list: how many are done out of how many, a progress bar,
// and the count in each state. Figures are computed by the page from rows it already holds;
// nothing here scores anything.

export interface StatusCounts {
  compliant: number;
  inProgress: number;
  gap: number;
  notStarted: number;
  /** The denominator: rows that count (not applicable / exempt ones already left out). */
  total: number;
}

/** Counts from any list of statuses in the platform's upper-case form. */
export function countStatuses(statuses: Array<string | null | undefined>): StatusCounts {
  const c: StatusCounts = { compliant: 0, inProgress: 0, gap: 0, notStarted: 0, total: statuses.length };
  for (const s of statuses) {
    if (s === "COMPLIANT") c.compliant++;
    else if (s === "IN_PROGRESS") c.inProgress++;
    else if (s === "GAP") c.gap++;
    else c.notStarted++;
  }
  return c;
}

export interface SummaryCell { key: string; value: React.ReactNode; label: string; color?: string }

/** The bar itself: headline figure, progress, and the cells on the right. */
export function SummaryBar({ label, done, total, progressLabel = "Progress", cells, children }: {
  label: string;
  done: number;
  total: number;
  progressLabel?: string;
  cells: SummaryCell[];
  children?: React.ReactNode;
}) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const complete = total > 0 && done === total;
  const bar = complete ? theme.color.successText : theme.color.accentText;
  return (
    <div style={{ ...card, padding: "14px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
      <div>
        <div style={{ ...text.eyebrow, marginBottom: 4 }}>{label}</div>
        <div style={{ ...text.stat, color: bar }}>{done} / {total}</div>
      </div>
      <div style={{ flex: 1, minWidth: 140 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, ...text.meta }}>
          <span>{progressLabel}</span><span>{pct}%</span>
        </div>
        <RulerProgress value={pct} fill={bar} label={progressLabel} />
      </div>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        {cells.map((c) => (
          <div key={c.key} style={{ textAlign: "center" }}>
            <div style={{ ...text.stat, fontSize: 18, color: c.color ?? theme.color.muted }}>{c.value}</div>
            <div style={text.meta}>{c.label}</div>
          </div>
        ))}
        {children}
      </div>
    </div>
  );
}

export function StatusSummary({ label, counts, novice, extraCells, children }: {
  /** "Controls compliant", "Clauses compliant", "Outcomes achieved". */
  label: string;
  counts: StatusCounts;
  novice?: boolean;
  extraCells?: SummaryCell[];
  /** Anything else for the right-hand end (the CE+ verification note). */
  children?: React.ReactNode;
}) {
  const table = novice ? CONTROL_STATUS_NOVICE : CONTROL_STATUS_EXPERT;
  const cells: SummaryCell[] = [
    { key: "NOT_STARTED", value: counts.notStarted, label: table.NOT_STARTED.label, color: theme.color.subtle },
    { key: "IN_PROGRESS", value: counts.inProgress, label: table.IN_PROGRESS.label, color: theme.color.warningText },
    { key: "GAP", value: counts.gap, label: table.GAP.label, color: theme.color.dangerText },
    { key: "COMPLIANT", value: counts.compliant, label: table.COMPLIANT.label, color: theme.color.successText },
    ...(extraCells ?? []),
  ];
  return <SummaryBar label={label} done={counts.compliant} total={counts.total} cells={cells}>{children}</SummaryBar>;
}
